import React, { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useWorkspaceLink } from '@/hooks/useWorkspaceSelection'
import { useUnreadCount } from '@/hooks/useNotifications'
import {
  LayoutDashboard,
  Home,
  Users,
  TrendingUp,
  Contact,
  MoreHorizontal,
  X,
  Kanban,
  Building2,
  Activity,
  CheckSquare,
  Phone,
  Calendar,
  Mail,
  BarChart3,
  CalendarDays,
  GitBranch,
  UserCog,
  Shield,
  Users2,
  Settings,
  Bell,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/utils/cn'
import { useAuthStore } from '@/store/authStore'

interface NavItem {
  key: string
  label: string
  path: string
  icon: LucideIcon
}

const PRIMARY_NAV: NavItem[] = [
  { key: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
  { key: 'leads', label: 'Leads', path: '/leads', icon: Users },
  { key: 'deals', label: 'Deals', path: '/deals', icon: TrendingUp },
  { key: 'contacts', label: 'Contacts', path: '/contacts', icon: Contact },
]

const MORE_NAV: NavItem[] = [
  { key: 'pipeline', label: 'Pipeline', path: '/pipeline', icon: Kanban },
  { key: 'organizations', label: 'Organizations', path: '/crm-organizations', icon: Building2 },
  { key: 'activities', label: 'Activities', path: '/activities', icon: Activity },
  { key: 'tasks', label: 'Tasks', path: '/tasks', icon: CheckSquare },
  { key: 'calls', label: 'Calls', path: '/calls', icon: Phone },
  { key: 'meetings', label: 'Meetings', path: '/meetings', icon: Calendar },
  { key: 'emails', label: 'Emails', path: '/emails', icon: Mail },
  { key: 'reports', label: 'Reports', path: '/reports', icon: BarChart3 },
  { key: 'calendar', label: 'Calendar', path: '/calendar', icon: CalendarDays },
  { key: 'workflows', label: 'Workflows', path: '/workflows', icon: GitBranch },
  { key: 'notifications', label: 'Notifications', path: '/notifications', icon: Bell },
]

const ADMIN_NAV: NavItem[] = [
  { key: 'admin-users', label: 'Users', path: '/admin/users', icon: UserCog },
  { key: 'admin-roles', label: 'Roles', path: '/admin/roles', icon: Shield },
  { key: 'admin-teams', label: 'Teams', path: '/admin/teams', icon: Users2 },
  { key: 'admin-settings', label: 'Settings', path: '/admin/settings', icon: Settings },
]

function isAdminUser(user: { role?: { name?: string } } | null): boolean {
  if (!user?.role?.name) return false
  const name = user.role.name.toLowerCase()
  return name === 'admin' || name === 'super_admin' || name === 'superadmin'
}

export function MobileBottomNav() {
  // Same rule as the sidebar: a tab switch is not a context change.
  const withWorkspace = useWorkspaceLink()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const location = useLocation()
  const { user } = useAuthStore()
  const isAdmin = isAdminUser(user)
  const unreadRaw: any = useUnreadCount()
  const unreadCount: number = typeof unreadRaw === 'number' ? unreadRaw : (unreadRaw?.unreadCount ?? unreadRaw?.data ?? 0)

  const path = location.pathname
  const moreItemActive = [...MORE_NAV, ...(isAdmin ? ADMIN_NAV : [])].some((item) =>
    path.startsWith(item.path)
  )

  const INACTIVE = '#9CA3AF'

  // One bottom-nav item (icon + label), with an optional unread badge.
  const NavItem = ({ to, icon: Icon, label, active, badge }: {
    to: string; icon: LucideIcon; label: string; active: boolean; badge?: number
  }) => (
    <NavLink to={withWorkspace(to)} className="flex flex-col items-center gap-1 px-2 py-1 flex-1 min-w-0">
      <div className="relative">
        <Icon size={20} style={{ color: active ? 'var(--accent)' : INACTIVE }} />
        {badge && badge > 0 ? (
          <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold text-white flex items-center justify-center"
            style={{ backgroundColor: 'var(--accent)' }}>
            {badge > 9 ? '9+' : badge}
          </span>
        ) : null}
      </div>
      <span className="text-[10px] font-medium leading-tight truncate max-w-full"
        style={{ color: active ? 'var(--accent)' : INACTIVE }}>{label}</span>
    </NavLink>
  )

  return (
    <>
      {/* Bottom nav bar — Home · Leads · More(center) · Alerts · Settings */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 lg:hidden glass-mobile-nav pb-safe">
        <div className="flex items-end justify-around px-1 pt-2 pb-1">
          <NavItem to="/home" icon={Home} label="Home" active={path === '/' || path === '/home'} />
          <NavItem to="/leads" icon={Users} label="Leads" active={path.startsWith('/leads')} />

          {/* Centre elevated More button */}
          <div className="flex flex-col items-center gap-1 -mt-5 flex-1">
            <button
              onClick={() => setDrawerOpen(true)}
              aria-label="More"
              className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform"
              style={{ backgroundColor: 'var(--accent)' }}>
              <MoreHorizontal size={22} className="text-white" />
            </button>
            <span className="text-[10px] font-medium" style={{ color: moreItemActive ? 'var(--accent)' : INACTIVE }}>More</span>
          </div>

          <NavItem to="/notifications" icon={Bell} label="Alerts" active={path.startsWith('/notifications')} badge={unreadCount} />
          <NavItem to="/admin/settings" icon={Settings} label="Settings" active={path.startsWith('/admin/settings')} />
        </div>
      </nav>

      {/* Slide-up drawer */}
      {drawerOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40 z-50 lg:hidden"
            onClick={() => setDrawerOpen(false)}
          />

          {/* Drawer */}
          <div className="fixed bottom-0 left-0 right-0 glass-mobile-panel rounded-t-2xl z-50 lg:hidden animate-slide-up max-h-[80vh] flex flex-col pb-safe">
            {/* Handle */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-mist dark:border-slate-700 flex-shrink-0">
              <span className="text-sm font-semibold text-navy dark:text-slate-100">
                Navigation
              </span>
              <button
                onClick={() => setDrawerOpen(false)}
                className="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Nav items */}
            <div className="flex-1 overflow-y-auto py-3 px-3">
              <DrawerSection label="Navigation" items={MORE_NAV} onClose={() => setDrawerOpen(false)} location={location} />
              {isAdmin && (
                <DrawerSection label="Admin" items={ADMIN_NAV} onClose={() => setDrawerOpen(false)} location={location} />
              )}
            </div>
          </div>
        </>
      )}
    </>
  )
}

function DrawerSection({
  label,
  items,
  onClose,
  location,
}: {
  label: string
  items: NavItem[]
  onClose: () => void
  location: ReturnType<typeof useLocation>
}) {
  const withWorkspace = useWorkspaceLink()
  return (
    <div className="mb-4">
      <p className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-widest px-3 mb-1">
        {label}
      </p>
      <div className="grid grid-cols-3 gap-1">
        {items.map((item) => {
          const Icon = item.icon
          const isActive = location.pathname.startsWith(item.path)
          return (
            <NavLink
              key={item.key}
              to={withWorkspace(item.path)}
              onClick={onClose}
              className={cn(
                'flex flex-col items-center gap-1 p-3 rounded-xl transition-colors text-center',
                isActive
                  ? 'bg-accent-50 font-semibold'
                  : 'text-gray-600 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-700'
              )}
              style={isActive ? { backgroundColor: 'var(--accent-50)', color: 'var(--accent)' } : undefined}
            >
              <Icon
                size={20}
                style={isActive ? { color: 'var(--accent)' } : undefined}
              />
              <span className="text-[10px] leading-tight">{item.label}</span>
            </NavLink>
          )
        })}
      </div>
    </div>
  )
}

export default MobileBottomNav
